/**
 * Per-face normal computation via Newell's method.
 *
 * Newell handles ngons and non-planar faces uniformly (a true cross product
 * only works for triangles), and is robust against zero-area or near-collinear
 * faces — degeneracy surfaces as a near-zero magnitude rather than NaN.
 *
 * Operates in object-local space; callers in world-space contexts must
 * transform by the object's normal matrix afterward.
 */

import { topologyFaceCount } from "./topology.ts";
import type { PolygonTopology } from "./types.ts";

/** Magnitude floor below which a face is considered degenerate. */
export const FACE_NORMAL_DEGENERATE_EPS = 1e-12;

/**
 * Compute the unit normal of `faceIndex` using Newell's method.
 * Returns `null` if the face is degenerate (zero-area or near-collinear).
 *
 * `out` is an optional 3-tuple to write into; provide one when iterating
 * every face to avoid per-call allocation.
 */
export function computeFaceNormal(
  vertices: Float32Array,
  topology: PolygonTopology,
  faceIndex: number,
  out?: [number, number, number],
): [number, number, number] | null {
  const { offsets, corners } = topology;
  const start = offsets[faceIndex];
  const end = offsets[faceIndex + 1];
  const size = end - start;
  if (size < 3) return null;

  let nx = 0;
  let ny = 0;
  let nz = 0;
  for (let j = 0; j < size; j++) {
    const a = corners[start + j];
    const b = corners[start + ((j + 1) % size)];
    const ax = vertices[a * 3];
    const ay = vertices[a * 3 + 1];
    const az = vertices[a * 3 + 2];
    const bx = vertices[b * 3];
    const by = vertices[b * 3 + 1];
    const bz = vertices[b * 3 + 2];
    nx += (ay - by) * (az + bz);
    ny += (az - bz) * (ax + bx);
    nz += (ax - bx) * (ay + by);
  }

  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (len < FACE_NORMAL_DEGENERATE_EPS) return null;
  const inv = 1 / len;
  if (out) {
    out[0] = nx * inv;
    out[1] = ny * inv;
    out[2] = nz * inv;
    return out;
  }
  return [nx * inv, ny * inv, nz * inv];
}

/**
 * Compute every face's unit normal once. `null` entries indicate degenerate
 * faces. Useful when an algorithm scans all faces and the per-call allocation
 * pressure of `computeFaceNormal` would dominate.
 */
export function computeAllFaceNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
): ([number, number, number] | null)[] {
  const count = topologyFaceCount(topology);
  const result: ([number, number, number] | null)[] = new Array(count);
  for (let f = 0; f < count; f++) {
    result[f] = computeFaceNormal(vertices, topology, f);
  }
  return result;
}
